"use client";

import { useState } from "react";
import Link from "next/link";
import { HiUser, HiClipboardCopy, HiCheck, HiQuestionMarkCircle } from "@/components/icons";
import ToolsCustomDevCTA from "@/components/ToolsCustomDevCTA";
import UnifiedFAQ from "@/components/UnifiedFAQ";
import ToolPageSchema from "@/components/schemas/ToolPageSchema";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type Style = "professional" | "creative" | "founder" | "minimal";

interface BioFields {
  role: string;
  company: string;
  interests: string;
  cta: string;
  location: string;
}

const STYLE_TEMPLATES: Record<Style, (fields: BioFields) => string> = {
  professional: (f) => {
    const parts: string[] = [];
    if (f.role) parts.push(f.role + (f.company ? ` at ${f.company}` : ""));
    if (f.interests) parts.push(f.interests);
    if (f.location) parts.push(`Based in ${f.location}`);
    if (f.cta) parts.push(f.cta);
    return parts.join(" | ");
  },
  creative: (f) => {
    const parts: string[] = [];
    if (f.role) parts.push(f.role);
    if (f.interests) parts.push(`Writing about ${f.interests}`);
    if (f.company) parts.push(`@ ${f.company}`);
    if (f.cta) parts.push(f.cta);
    if (f.location) parts.push(`From ${f.location}`);
    return parts.join(" ✦ ");
  },
  founder: (f) => {
    const parts: string[] = [];
    if (f.role && f.company) parts.push(`${f.role} — Building ${f.company}`);
    else if (f.role) parts.push(f.role);
    if (f.interests) parts.push(`Sharing insights on ${f.interests}`);
    if (f.cta) parts.push(f.cta);
    if (f.location) parts.push(f.location);
    return parts.join(" · ");
  },
  minimal: (f) => {
    const parts: string[] = [];
    if (f.role) parts.push(f.role);
    if (f.interests) parts.push(f.interests);
    if (f.cta) parts.push(f.cta);
    return (
      parts.join(". ") + (parts.length > 0 && !parts[parts.length - 1].endsWith(".") ? "." : "")
    );
  },
};

const STYLE_LABELS: Record<Style, { label: string; description: string }> = {
  professional: { label: "Professional", description: "Clean, pipe-separated, formal tone" },
  creative: { label: "Creative", description: "Fun separators, energetic feel" },
  founder: { label: "Founder", description: "Builder-first language, startup vibe" },
  minimal: { label: "Minimal", description: "Short, clean, no clutter" },
};

const MAX_BIO_LENGTH = 160;

const FAQ_ITEMS = [
  {
    question: "How long can a Twitter bio be?",
    answer:
      "Twitter / X allows a maximum of 160 characters in the bio field. This tool tracks your character count in real time and warns you if the generated bio exceeds the limit.",
  },
  {
    question: "Does this use AI to generate the bio?",
    answer:
      "No. This tool uses predefined templates that combine your inputs (role, company, interests, CTA, location) with style-specific formatting. There is no AI or external API call — everything runs instantly in your browser.",
  },
  {
    question: "What should I put in the CTA field?",
    answer:
      "A CTA (Call to Action) is a short phrase that tells visitors what to do next. Examples: 'DM me for collabs', 'Newsletter link below', 'Coaching spots open', or 'Check my pinned tweet'. Keep it brief — it is sharing space with the rest of your bio.",
  },
  {
    question: "Which style should I choose?",
    answer:
      "Professional works well for corporate roles, recruiters, and B2B audiences. Creative suits artists, writers, and influencers. Founder is ideal for startup builders and entrepreneurs. Minimal is great if you want a clean, no-fuss presence.",
  },
  {
    question: "Can I edit the generated bio before copying?",
    answer:
      "Yes. After generating, click on the bio preview text to edit it directly. The character counter updates as you type so you can refine the output to fit within 160 characters.",
  },
];

export default function TwitterBioGeneratorClient() {
  const [fields, setFields] = useState<BioFields>({
    role: "",
    company: "",
    interests: "",
    cta: "",
    location: "",
  });
  const [style, setStyle] = useState<Style>("professional");
  const [editableBio, setEditableBio] = useState("");
  const [generated, setGenerated] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleGenerate = () => {
    const bio = STYLE_TEMPLATES[style](fields).slice(0, MAX_BIO_LENGTH * 2);
    setEditableBio(bio);
    setGenerated(true);
    setCopied(false);
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(editableBio);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const charCount = editableBio.length;
  const isOverLimit = charCount > MAX_BIO_LENGTH;

  const updateField = (key: keyof BioFields, value: string) => {
    setFields((prev) => ({ ...prev, [key]: value }));
    setGenerated(false);
  };

  return (
    <div className="container px-4 py-12 mx-auto max-w-4xl">
      <ToolPageSchema
        name="Twitter Bio Generator"
        description="Generate a compelling Twitter/X bio from templates. Fill in your role, interests, and CTA, choose a style, and get a 160-character bio instantly."
        url="https://plugmonkey.xyz/tool/twitter-bio-generator/"
        breadcrumbs={[
          { name: "Home", url: "https://plugmonkey.xyz/" },
          { name: "Free Tools", url: "https://plugmonkey.xyz/tools/" },
          {
            name: "Twitter Bio Generator",
            url: "https://plugmonkey.xyz/tool/twitter-bio-generator/",
          },
        ]}
        faq={FAQ_ITEMS}
      />

      {/* Hero */}
      <div className="flex items-center gap-4 mb-8 mt-6">
        <div className="p-3 bg-sky-500/10 rounded-full text-sky-500">
          <HiUser className="w-8 h-8" />
        </div>
        <div>
          <h1 className="text-3xl md:text-4xl font-bold">Twitter Bio Generator</h1>
          <p className="text-muted-foreground mt-1">
            Fill in your details, pick a style, copy your bio.
          </p>
        </div>
      </div>

      <div className="mb-8 text-muted-foreground leading-relaxed space-y-3 max-w-3xl">
        <p>
          Your Twitter bio is often the first thing people read when deciding whether to follow you.
          A good bio communicates who you are, what you talk about, and why someone should care —
          all within 160 characters.
        </p>
        <p>
          Fill in your details below, pick a style that matches your brand, and generate a bio
          instantly. You can edit the result directly before copying.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Form */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Your Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="bio-role">Role / Job Title</Label>
                <Input
                  id="bio-role"
                  placeholder="e.g. Product Designer, Founder, Content Creator"
                  value={fields.role}
                  onChange={(e) => updateField("role", e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="bio-company">
                  Company / Project{" "}
                  <span className="text-muted-foreground text-xs">(optional)</span>
                </Label>
                <Input
                  id="bio-company"
                  placeholder="e.g. Acme Inc, PlugMonkey, My Newsletter"
                  value={fields.company}
                  onChange={(e) => updateField("company", e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="bio-interests">Topics / Interests</Label>
                <Input
                  id="bio-interests"
                  placeholder="e.g. SaaS, AI, marketing, productivity"
                  value={fields.interests}
                  onChange={(e) => updateField("interests", e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="bio-cta">
                  Call to Action <span className="text-muted-foreground text-xs">(optional)</span>
                </Label>
                <Input
                  id="bio-cta"
                  placeholder="e.g. DM for collabs, Newsletter link below"
                  value={fields.cta}
                  onChange={(e) => updateField("cta", e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="bio-location">
                  Location <span className="text-muted-foreground text-xs">(optional)</span>
                </Label>
                <Input
                  id="bio-location"
                  placeholder="e.g. San Francisco, Remote"
                  value={fields.location}
                  onChange={(e) => updateField("location", e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Style picker */}
          <Card>
            <CardHeader>
              <CardTitle>Choose a Style</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-3">
              {(Object.keys(STYLE_LABELS) as Style[]).map((s) => (
                <button
                  key={s}
                  onClick={() => {
                    setStyle(s);
                    setGenerated(false);
                  }}
                  className={`rounded-xl border p-3 text-left transition-all ${
                    style === s
                      ? "border-sky-500 bg-sky-500/5 shadow-sm"
                      : "border-border hover:border-sky-400"
                  }`}
                  aria-pressed={style === s}
                >
                  <div className="font-semibold text-sm">{STYLE_LABELS[s].label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {STYLE_LABELS[s].description}
                  </div>
                </button>
              ))}
            </CardContent>
          </Card>

          <Button
            className="w-full h-12 text-base font-bold"
            onClick={handleGenerate}
            disabled={!fields.role && !fields.interests}
          >
            Generate Bio
          </Button>
        </div>

        {/* Preview */}
        <div>
          <div className="sticky top-6 space-y-6">
            <Card
              className={`border-2 ${generated ? (isOverLimit ? "border-red-400" : "border-sky-400") : "border-border"} transition-colors`}
            >
              <CardHeader>
                <CardTitle>Live Preview</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {generated ? (
                  <>
                    {/* Simulated Twitter profile card */}
                    <div className="rounded-xl bg-muted/40 p-5 space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-sky-500/20 flex items-center justify-center text-sky-500 font-bold text-xl">
                          {fields.role ? fields.role[0].toUpperCase() : "U"}
                        </div>
                        <div>
                          <div className="font-bold text-sm">Your Name</div>
                          <div className="text-muted-foreground text-xs">@yourhandle</div>
                        </div>
                      </div>
                      <textarea
                        id="bio-preview"
                        name="bio-preview"
                        className={`w-full text-sm bg-transparent border-none outline-none resize-none leading-relaxed ${isOverLimit ? "text-red-500" : ""}`}
                        rows={4}
                        value={editableBio}
                        onChange={(e) => setEditableBio(e.target.value)}
                        aria-label="Editable bio preview"
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <span
                        className={`text-sm font-mono font-medium ${isOverLimit ? "text-red-500" : charCount > 140 ? "text-yellow-500" : "text-muted-foreground"}`}
                      >
                        {charCount}/{MAX_BIO_LENGTH} characters
                        {isOverLimit && " — too long!"}
                      </span>
                      <Button
                        size="sm"
                        onClick={handleCopy}
                        disabled={isOverLimit}
                        className="gap-1"
                      >
                        {copied ? (
                          <>
                            <HiCheck className="h-4 w-4" /> Copied!
                          </>
                        ) : (
                          <>
                            <HiClipboardCopy className="h-4 w-4" /> Copy Bio
                          </>
                        )}
                      </Button>
                    </div>

                    {isOverLimit && (
                      <p className="text-xs text-red-500">
                        Trim {charCount - MAX_BIO_LENGTH} character
                        {charCount - MAX_BIO_LENGTH > 1 ? "s" : ""} to fit Twitter's 160-character
                        limit.
                      </p>
                    )}
                  </>
                ) : (
                  <div className="text-center py-10 text-muted-foreground">
                    <HiUser className="w-12 h-12 mx-auto mb-3 opacity-20" />
                    <p className="text-sm">Fill in your details and click Generate Bio</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="bg-card border rounded-xl p-6">
              <h2 className="font-bold text-lg mb-2">Manage your followers too?</h2>
              <p className="text-muted-foreground text-sm mb-4">
                <strong>Unfollow Pro for X</strong> helps you audit and clean up who you follow so
                your feed stays relevant.
              </p>
              <Button asChild variant="secondary" className="w-full">
                <Link href="/product/x-unfollow-pro/">View Extension</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <section className="mt-16 pt-12 border-t border-border">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl border border-border bg-card p-8 md:p-12 shadow-sm">
            <div className="flex items-center gap-4 mb-10">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <HiQuestionMarkCircle className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Frequently Asked Questions</h2>
                <p className="text-muted-foreground text-sm">
                  Click on a question to view the answer.
                </p>
              </div>
            </div>
            <UnifiedFAQ items={FAQ_ITEMS} showSupportLink={false} className="space-y-4" />
          </div>
        </div>
      </section>

      <ToolsCustomDevCTA />
    </div>
  );
}
